import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';

/**
 * `null` when leaving is fine; otherwise the prompt to confirm, or `block`
 * when leaving must not happen at all (work in flight that a confirm can't stop).
 */
type Guard = () => { message: string; block: boolean } | null;

interface UnsavedGuardContextValue {
  register: (guard: Guard) => () => void;
  confirmNavigate: () => boolean;
}

/**
 * The default is PERMISSIVE on purpose: dozens of test files mount pages in a
 * plain `MemoryRouter` without the provider, and a page registering a guard
 * there must behave exactly as before — no prompt, no error. Only a tree under
 * `UnsavedGuardProvider` (the app shell) actually enforces anything.
 */
const UnsavedGuardContext = createContext<UnsavedGuardContextValue>({
  register: () => () => {},
  confirmNavigate: () => true,
});

/**
 * Shell-level unsaved-changes guard (ADR-030). `BrowserRouter` offers no
 * `useBlocker`, so instead of intercepting the router, dirty pages register a
 * guard here and nav surfaces consult `useConfirmNavigate()` before letting a
 * click through — the `offers/` confirmNavigate → preventDefault idiom,
 * generalized. Browser Back/Forward and programmatic navigation stay
 * unguarded (accepted gap, see the ADR).
 */
export function UnsavedGuardProvider({ children }: { children: ReactNode }) {
  const guards = useRef(new Set<Guard>());

  const register = useCallback((guard: Guard) => {
    guards.current.add(guard);
    return () => {
      guards.current.delete(guard);
    };
  }, []);

  const confirmNavigate = useCallback(() => {
    const decisions = [...guards.current].map((guard) => guard());
    // A hard block wins over any prompt registered before it.
    if (decisions.some((d) => d?.block)) return false;
    const prompt = decisions.find((d) => d !== null);
    return prompt ? window.confirm(prompt.message) : true;
  }, []);

  const value = useMemo(() => ({ register, confirmNavigate }), [register, confirmNavigate]);
  return <UnsavedGuardContext.Provider value={value}>{children}</UnsavedGuardContext.Provider>;
}

/**
 * Marks the mounted page dirty for the shell guard AND owns the reload prompt
 * (`beforeunload`) while dirty — one hook instead of a per-editor effect. The
 * `beforeunload` half works with or without the provider, so pages keep their
 * reload prompt even in trees that never mounted the shell. `block` turns the
 * in-app prompt into a hard stop (no confirm) for work a confirm can't cancel.
 */
export function useUnsavedChangesGuard(
  dirty: boolean,
  message = 'Discard unsaved changes?',
  block = false,
): void {
  const { register } = useContext(UnsavedGuardContext);
  // The registered guard is stable across renders and reads the latest
  // dirty/message through the ref — re-registering per render would thrash
  // the set for nothing.
  const guardRef = useRef<Guard>(() => null);
  guardRef.current = () => (dirty ? { message, block } : null);
  useEffect(() => register(() => guardRef.current()), [register]);

  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);
}

/**
 * Consulted by nav surfaces before navigating: runs the registered guards,
 * `window.confirm`s the first non-null message, and returns whether the
 * navigation may proceed. Callers `e.preventDefault()` on `false`.
 */
export function useConfirmNavigate(): () => boolean {
  return useContext(UnsavedGuardContext).confirmNavigate;
}
